import { Component } from '@theme/component';
import { throttle } from '@theme/utilities';

/**
 * Drives the Admira carousel controls (SPEC §6.2).
 *
 * The rail and the buttons are both derived from the track's `scrollLeft`,
 * never from an index this component maintains. A scroll-snap track can also be
 * moved by a trackpad, a scrollbar drag, a touch flick or Tab-ing to an offscreen
 * card, and an internal index desynchronises on every one of those.
 *
 * @extends {Component<{
 *   railFill: HTMLElement,
 *   previousButton: HTMLButtonElement,
 *   nextButton: HTMLButtonElement,
 *   track?: HTMLElement,
 * }>}
 */
class CarouselControls extends Component {
  requiredRefs = ['railFill', 'previousButton', 'nextButton'];

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {((...args: any[]) => any) & { cancel(): void } | null} */
  #onScroll = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#track = this.#resolveTrack();
    if (!this.#track) return;

    // Throttled and passive: this fires on every scroll frame of a component
    // that exists to be scrolled. PERF-BUDGET §6.2.
    this.#onScroll = throttle(() => this.#sync(), 100);
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    // The throttle has no trailing call, so the last frames of a scroll could
    // be dropped and the filling rail stop short of the end; `scrollend`
    // settles it on the final position.
    this.#track.addEventListener('scrollend', this.#onScrollEnd, { passive: true });

    // A resize changes how much of the track is visible, which changes both the
    // fill width and whether the forward button should still be enabled.
    // observe() also delivers a first callback after the next layout and before
    // paint, which is the initial sync. Calling #sync() here as well read the
    // track's widths mid-upgrade and forced a whole-page style recalc (85 ms on
    // a throttled phone; audit 2026-10-08).
    this.#resizeObserver = new ResizeObserver(() => this.#sync());
    this.#resizeObserver.observe(this.#track);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#track && this.#onScroll) {
      this.#track.removeEventListener('scroll', this.#onScroll);
      this.#track.removeEventListener('scrollend', this.#onScrollEnd);
      this.#onScroll.cancel();
    }

    this.#resizeObserver?.disconnect();
  }

  #onScrollEnd = () => this.#sync();

  /** Scrolls back by one item. */
  previous() {
    this.#scrollByItem(-1);
  }

  /** Scrolls forward by one item. */
  next() {
    this.#scrollByItem(1);
  }

  /**
   * The track is either a `ref="track"` descendant or an element named by
   * `data-track-id`, so a controls block can sit outside the scroller it drives —
   * which it does in every design that puts the controls in the section header.
   *
   * @returns {HTMLElement | null}
   */
  #resolveTrack() {
    const { track } = this.refs;
    if (track) return track;

    const id = this.dataset.trackId;
    if (!id) return null;

    const element = document.getElementById(id);
    return element instanceof HTMLElement ? element : null;
  }

  /**
   * One "item" is the first child's outer width plus the track's column gap.
   * Reading it from the DOM rather than a setting means the controls stay correct
   * when a section changes its card size, and it degrades to a viewport-width
   * scroll if the track is empty.
   *
   * @returns {number}
   */
  #itemWidth() {
    const track = this.#track;
    if (!track) return 0;

    const first = track.firstElementChild;
    if (!(first instanceof HTMLElement)) return track.clientWidth;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  /** @param {number} direction -1 back, 1 forward. */
  #scrollByItem(direction) {
    const track = this.#track;
    if (!track) return;

    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({
      left: this.#itemWidth() * direction,
      behavior: reduced ? 'auto' : 'smooth',
    });
  }

  /**
   * Recomputes the rail fill and the two disabled states from the track's
   * current scroll position.
   */
  #sync() {
    const track = this.#track;
    if (!track) return;

    const { railFill, previousButton, nextButton } = this.refs;
    const visible = track.clientWidth;
    const total = track.scrollWidth;

    // Math.abs so a right-to-left track, where scrollLeft runs negative, still
    // produces a forward-growing rail.
    const offset = Math.abs(track.scrollLeft);
    const scrollable = total - visible;

    if (scrollable <= 1) {
      // Nothing to scroll: a full rail is more honest than an empty one, and
      // both buttons are dead ends.
      railFill.style.inlineSize = '100%';
      previousButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    // The fill is how much of the track has been seen: it starts at the visible
    // fraction and grows to the whole rail at the end, rather than sliding a
    // fixed thumb along (designer feedback 2026-10-15).
    const fraction = Math.min(visible / total, 1);
    const progress = Math.min(offset / scrollable, 1);

    railFill.style.inlineSize = `${(fraction + progress * (1 - fraction)) * 100}%`;

    // A one-pixel tolerance: fractional layout means scrollLeft rarely lands
    // exactly on either end.
    previousButton.disabled = offset <= 1;
    nextButton.disabled = offset >= scrollable - 1;
  }
}

if (!customElements.get('carousel-controls')) {
  customElements.define('carousel-controls', CarouselControls);
}
